import React, { forwardRef, useImperativeHandle, useState, useEffect, useMemo } from "react";
import { Row, Col, FormGroup, Label, Input } from "reactstrap";
import Select from "components/Select";
import DollarInput from "../DollarInput";
import { getActiveColorStyle } from "../../utils/colorUtils";
import { getSelectStyles } from "../../utils/selectStyles";
import { getTheme2SearchFilterSingleSelectStyles } from "utils/theme2MultiSelect";
import { formatPriceWithDollar, toNumber, formatPercentageInput, displayPercentage } from "../../utils/currency";

const PortfolioPastDueFilters = forwardRef(({ data, activeColor, isTheme2 = false, portfolioId, updateMutation }, ref) => {
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const [isLightTheme, setIsLightTheme] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  
  const [formData, setFormData] = useState({
    include_down_past: false,
    include_misc_past: false,
    include_defer_down_past: false,
    include_insurance_past: false,
    include_regular_past: false,
    include_other_side_past: false,
    tolorance: formatPriceWithDollar(0),
    tolorance_percent: "0",
  });

  useEffect(() => {
    if (data) {
      setFormData({
        include_down_past: data.include_down_past ?? false,
        include_misc_past: data.include_misc_past ?? false,
        include_defer_down_past: data.include_defer_down_past ?? false,
        include_insurance_past: data.include_insurance_past ?? false,
        include_regular_past: data.include_regular_past ?? false,
        include_other_side_past: data.include_other_side_past ?? false,
        tolorance: formatPriceWithDollar(data.tolorance ?? 0),
        tolorance_percent: data.tolorance_percent ? String(data.tolorance_percent) : "0",
      });
    }
  }, [data]);

  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains('white-content'));
    };
    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const selectStyles = useMemo(
    () =>
      isTheme2
        ? getTheme2SearchFilterSingleSelectStyles(activeColorStyle, true)
        : getSelectStyles(activeColorStyle, isLightTheme),
    [activeColorStyle, isLightTheme, isTheme2]
  );
  const inputStyle = isTheme2
    ? { textAlign: "right", fontSize: "14px" }
    : { textAlign: "right" };
  const dollarInputStyle = isTheme2 ? { fontSize: "14px" } : undefined;

  const yesNoOptions = [
    { value: false, label: "No" },
    { value: true, label: "Yes" },
  ];

  const handlePercentChange = (name, value) => {
    const formatted = formatPercentageInput(value);
    setFormData(prev => ({ ...prev, [name]: formatted }));
  };

  // Expose handleSave to parent via ref
  useImperativeHandle(ref, () => ({
    handleSave: async () => {
      try {
        const payload = {
          include_down_past: formData.include_down_past,
          include_misc_past: formData.include_misc_past,
          include_defer_down_past: formData.include_defer_down_past,
          include_insurance_past: formData.include_insurance_past,
          include_regular_past: formData.include_regular_past,
          include_other_side_past: formData.include_other_side_past,
          tolorance: toNumber(formData.tolorance),
          tolorance_percent: toNumber(formData.tolorance_percent),
        };

        await updateMutation.mutateAsync({
          id: portfolioId,
          data: payload
        });

        return { success: true };
      } catch (error) {
        console.error("Error saving past due filters:", error);
        return { success: false, error };
      }
    }
  }));
  
  return (
    <div className="card" style={{ backgroundColor: activeColorStyle.backgroundColor }}>
      <div className="card-header font-weight-bold text-uppercase">CONFIGURE HOW ACCOUNT PAST DUES ARE MONITORED</div>
      <div className="card-body">
        <Row>
          <Col md="4">
            <FormGroup>
              <Label>Include Down Pmts In Past Dues</Label>
              <Select
                activeColor={activeColor}
                styles={selectStyles}
                className="react-select"
                placeholder="Select"
                name="include_down_past"
                isSearchable={false}
                closeMenuOnSelect={true}
                value={yesNoOptions.find(opt => opt.value === formData.include_down_past)}
                onChange={(value) => setFormData(prev => ({ ...prev, include_down_past: value?.value ?? false }))}
                options={yesNoOptions}
              />
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label>Include Misc Pmts In Pst Due</Label>
              <Select
                activeColor={activeColor}
                styles={selectStyles}
                className="react-select"
                placeholder="Select"
                name="include_misc_past"
                isSearchable={false}
                closeMenuOnSelect={true}
                value={yesNoOptions.find(opt => opt.value === formData.include_misc_past)}
                onChange={(value) => setFormData(prev => ({ ...prev, include_misc_past: value?.value ?? false }))}
                options={yesNoOptions}
              />
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label>Include Defer Down Pmts In Pst Due</Label>
              <Select
                activeColor={activeColor}
                styles={selectStyles}
                className="react-select"
                placeholder="Select"
                name="include_defer_down_past"
                isSearchable={false}
                closeMenuOnSelect={true}
                value={yesNoOptions.find(opt => opt.value === formData.include_defer_down_past)}
                onChange={(value) => setFormData(prev => ({ ...prev, include_defer_down_past: value?.value ?? false }))}
                options={yesNoOptions}
              />
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label>Include Insurance Pmts In Pst Due</Label>
              <Select
                activeColor={activeColor}
                styles={selectStyles}
                className="react-select"
                placeholder="Select"
                name="include_insurance_past"
                isSearchable={false}
                closeMenuOnSelect={true}
                value={yesNoOptions.find(opt => opt.value === formData.include_insurance_past)}
                onChange={(value) => setFormData(prev => ({ ...prev, include_insurance_past: value?.value ?? false }))}
                options={yesNoOptions}
              />
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label>Include Regular Pmts In Pst Due</Label>
              <Select
                activeColor={activeColor}
                styles={selectStyles}
                className="react-select"
                placeholder="Select"
                name="include_regular_past"
                isSearchable={false}
                closeMenuOnSelect={true}
                value={yesNoOptions.find(opt => opt.value === formData.include_regular_past)}
                onChange={(value) => setFormData(prev => ({ ...prev, include_regular_past: value?.value ?? false }))}
                options={yesNoOptions}
              />
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label>Inc Other/Side Note Pmts In Pst Due</Label>
              <Select
                activeColor={activeColor}
                styles={selectStyles}
                className="react-select"
                placeholder="Select"
                name="include_other_side_past"
                isSearchable={false}
                closeMenuOnSelect={true}
                value={yesNoOptions.find(opt => opt.value === formData.include_other_side_past)}
                onChange={(value) => setFormData(prev => ({ ...prev, include_other_side_past: value?.value ?? false }))}
                options={yesNoOptions}
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label>Tolerance Amount</Label>
              <DollarInput
                name="tolorance"
                className="form-control"
                style={dollarInputStyle}
                value={formData.tolorance}
                onChange={(value) => setFormData(prev => ({ ...prev, tolorance: value }))}
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label>Tolerance Percent</Label>
              <Input
                name="tolorance_percent"
                type="text"
                className="form-control"
                value={displayPercentage(formData.tolorance_percent, focusedField === 'tolorance_percent')}
                onChange={(e) => handlePercentChange('tolorance_percent', e.target.value)}
                onFocus={() => setFocusedField('tolorance_percent')}
                onBlur={() => setFocusedField(null)}
                style={inputStyle}
                placeholder="0%"
              />
            </FormGroup>
          </Col>
        </Row>
      </div>
    </div>
  );
});

PortfolioPastDueFilters.displayName = "PortfolioPastDueFilters";

export default PortfolioPastDueFilters;

